<script lang="ts">
/**
 * Root layout coordinator.
 *
 * Wraps Nuxt UI's `UDashboardGroup` and adds a reactive layout state that
 * child components (`AppFooter`, `AppPagePanel`) use to coordinate spacing.
 * When an `AppFooter` mounts inside an `AppLayout`, it registers its
 * measured height, and `AppPagePanel` automatically adjusts its body padding.
 *
 * @example
 * ```vue
 * <AppLayout>
 *   <AppSidebar>...</AppSidebar>
 *   <AppPagePanel>...</AppPagePanel>
 *   <AppFooter>...</AppFooter>
 * </AppLayout>
 * ```
 */
export interface AppLayoutProps {
  /** Storage type for persisting sidebar state (`"cookie"` or `"local"`). @defaultValue `"local"` */
  storage?: "cookie" | "local"
  /** CSS unit for resizable panel widths. @defaultValue `"rem"` */
  unit?: "rem" | "%" | "px"
}
</script>

<script setup lang="ts">
import { reactive, provide } from "vue"

withDefaults(defineProps<AppLayoutProps>(), {
  storage: "local",
  unit: "rem",
})

const state = reactive({
  headerHeight: 0,
  footerHeight: 0,
})

provide("app-layout", {
  state,
  registerHeader(height: number) {
    state.headerHeight = height
  },
  registerFooter(height: number) {
    state.footerHeight = height
  },
})
</script>

<template>
  <UDashboardGroup :storage="storage" :unit="unit">
    <slot />
  </UDashboardGroup>
</template>
